import { BioCrateRarity } from '../types';

/**
 * Procedural Web Audio API sound synthesis engine.
 * Generates futuristic, high-impact unboxing audio SFX on-the-fly with zero external MP3 dependencies.
 */
class GamificationAudioSynthesizer {
  private audioCtx: AudioContext | null = null;
  private isMuted: boolean = false;
  private activeOscillators: { osc: OscillatorNode; gain: GainNode }[] = [];
  private lastUiClickTime: number = 0;
  private isGlobalTapListenerAttached: boolean = false;

  constructor() {
    if (typeof window !== 'undefined') {
      const storedPro = localStorage.getItem('pro_audio_enabled');
      const storedSfx = localStorage.getItem('gamification_sfx_muted');
      this.isMuted = storedPro === 'false' || storedSfx === 'true';
      this.initGlobalTapListener();
    }
  }

  public unlockAudio() {
    if (this.isMuted) return;
    try {
      if (!this.audioCtx && typeof window !== 'undefined') {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
          this.audioCtx = new AudioContextClass();
        }
      }
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume().catch(() => {});
      }
    } catch (e) {}
  }

  public initGlobalTapListener() {
    if (typeof window === 'undefined' || this.isGlobalTapListenerAttached) return;
    this.isGlobalTapListenerAttached = true;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let trackedPointerId: number | null = null;

    const handlePointerDown = (e: PointerEvent) => {
      // Primary button or touch only (ignore right-click or secondary mouse buttons)
      if (e.button !== 0 && e.pointerType === 'mouse') return;
      startX = e.clientX;
      startY = e.clientY;
      startTime = performance.now();
      trackedPointerId = e.pointerId;

      this.unlockAudio();
    };

    const handlePointerUp = (e: PointerEvent) => {
      if (trackedPointerId !== null && e.pointerId !== trackedPointerId) return;
      trackedPointerId = null;

      const elapsed = performance.now() - startTime;
      const distX = Math.abs(e.clientX - startX);
      const distY = Math.abs(e.clientY - startY);
      const dist = Math.hypot(distX, distY);

      // A tap is defined as releasing within 500ms and less than 14px of movement
      // This cleanly differentiates taps from scrolling, dragging, swiping, or text selecting
      if (dist < 14 && elapsed < 500) {
        this.playUiClick();
      }
    };

    const handlePointerCancel = () => {
      trackedPointerId = null;
    };

    const handleClick = () => {
      // Catches keyboard activations (Enter / Space on buttons) or synthetic click dispatches.
      // Already-handled pointer taps will be deduplicated by playUiClick's 35ms throttle.
      this.playUiClick();
    };

    window.addEventListener('pointerdown', handlePointerDown, { capture: true, passive: true });
    window.addEventListener('pointerup', handlePointerUp, { capture: true, passive: true });
    window.addEventListener('pointercancel', handlePointerCancel, { capture: true, passive: true });
    window.addEventListener('click', handleClick, { capture: true, passive: true });
  }

  private getContext(): AudioContext | null {
    if (this.isMuted) return null;
    if (typeof window === 'undefined') return null;

    try {
      if (!this.audioCtx) {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
          this.audioCtx = new AudioContextClass();
        }
      }
      if (this.audioCtx && this.audioCtx.state === 'suspended') {
        this.audioCtx.resume().catch(() => {});
      }
      return this.audioCtx;
    } catch (e) {
      console.warn('[AudioSynth] Web Audio initialization warning:', e);
      return null;
    }
  }

  public setMuted(muted: boolean) {
    this.isMuted = muted;
    if (typeof window !== 'undefined') {
      localStorage.setItem('gamification_sfx_muted', String(muted));
      localStorage.setItem('pro_audio_enabled', String(!muted));
    }
    if (muted) {
      this.stopAll();
    }
  }

  public getIsMuted(): boolean {
    return this.isMuted;
  }

  public stopAll() {
    this.activeOscillators.forEach(({ osc, gain }) => {
      try {
        gain.gain.setValueAtTime(0, this.audioCtx?.currentTime || 0);
        osc.stop();
        osc.disconnect();
      } catch (e) {}
    });
    this.activeOscillators = [];
  }

  /**
   * 1. Pre-Charge Pulse: Rising drone + pulsing energy sweep leading into impact frame
   */
  public playPreChargePulse(durationMs: number = 1800) {
    const ctx = this.getContext();
    if (!ctx) return;

    this.stopAll();

    const now = ctx.currentTime;
    const duration = durationMs / 1000;

    // Sub-oscillator for deep vibration
    const subOsc = ctx.createOscillator();
    const subGain = ctx.createGain();
    subOsc.type = 'sawtooth';
    subOsc.frequency.setValueAtTime(45, now);
    subOsc.frequency.exponentialRampToValueAtTime(180, now + duration);

    // Lowpass filter that opens up over time
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(150, now);
    filter.frequency.exponentialRampToValueAtTime(2400, now + duration);
    filter.Q.setValueAtTime(4, now);

    // Gain envelope with rapid pulsing (LFO)
    subGain.gain.setValueAtTime(0.01, now);
    subGain.gain.linearRampToValueAtTime(0.25, now + duration * 0.8);
    subGain.gain.exponentialRampToValueAtTime(0.4, now + duration);

    // LFO for pulsing tension
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.type = 'sine';
    lfo.frequency.setValueAtTime(4, now);
    lfo.frequency.exponentialRampToValueAtTime(16, now + duration); // Accelerates
    lfoGain.gain.setValueAtTime(0.15, now);

    lfo.connect(subGain.gain);
    subOsc.connect(filter);
    filter.connect(subGain);
    subGain.connect(ctx.destination);

    subOsc.start(now);
    lfo.start(now);
    subOsc.stop(now + duration);
    lfo.stop(now + duration);

    this.activeOscillators.push({ osc: subOsc, gain: subGain });
  }

  /**
   * 2. Impact Explosion: Heavy bass thud + white noise impact burst on crate shatter
   */
  public playImpactExplosion() {
    const ctx = this.getContext();
    if (!ctx) return;

    this.stopAll();
    const now = ctx.currentTime;

    // Bass Drop Sine Kick
    const kickOsc = ctx.createOscillator();
    const kickGain = ctx.createGain();
    kickOsc.type = 'sine';
    kickOsc.frequency.setValueAtTime(160, now);
    kickOsc.frequency.exponentialRampToValueAtTime(30, now + 0.35);

    kickGain.gain.setValueAtTime(0.6, now);
    kickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

    kickOsc.connect(kickGain);
    kickGain.connect(ctx.destination);
    kickOsc.start(now);
    kickOsc.stop(now + 0.45);

    // Noise Transient Burst
    const bufferSize = ctx.sampleRate * 0.25;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (ctx.sampleRate * 0.05));
    }

    const noiseSource = ctx.createBufferSource();
    noiseSource.buffer = buffer;

    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.setValueAtTime(1200, now);
    noiseFilter.Q.setValueAtTime(2, now);

    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.3, now);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

    noiseSource.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(ctx.destination);

    noiseSource.start(now);
  }

  /**
   * 3. Rarity-Dependent Harmonic Fanfare: Plays when card/reward flips open
   */
  public playRarityFanfare(rarity: BioCrateRarity) {
    const ctx = this.getContext();
    if (!ctx) return;

    const now = ctx.currentTime;

    switch (rarity) {
      case 'common': {
        // Simple crisp chime (C5)
        this.playTone(523.25, 'sine', 0.4, 0.25, now);
        break;
      }
      case 'rare': {
        // Dual-tone harmonic chime (C5 + G5)
        this.playTone(523.25, 'triangle', 0.5, 0.25, now);
        this.playTone(783.99, 'sine', 0.6, 0.2, now + 0.08);
        break;
      }
      case 'epic': {
        // Fast ascending 4-note synth arpeggio (A4 -> C#5 -> E5 -> A5)
        const notes = [440.00, 554.37, 659.25, 880.00];
        notes.forEach((freq, idx) => {
          this.playTone(freq, 'triangle', 0.6, 0.22, now + idx * 0.07);
        });
        break;
      }
      case 'legendary': {
        // Golden fanfare chord with brass sweep (F4 -> A4 -> C5 -> F5 -> A5)
        const notes = [349.23, 440.00, 523.25, 698.46, 880.00];
        notes.forEach((freq, idx) => {
          this.playTone(freq, 'sawtooth', 0.8, 0.15, now + idx * 0.08, 1200);
        });
        // Sustained high shimmer
        this.playTone(1046.50, 'sine', 1.2, 0.18, now + 0.35);
        break;
      }
      case 'mythic': {
        // Cyber celestial harmonic sweep (Matrix ascension)
        const chords = [523.25, 659.25, 783.99, 1046.50, 1318.51, 1567.98];
        chords.forEach((freq, idx) => {
          this.playTone(freq, 'sine', 1.4, 0.16, now + idx * 0.06);
        });
        // Resonant sweep
        this.playSweep(200, 2400, 0.8, now);
        break;
      }
    }
  }

  /**
   * 4. Nutri-Token Counter Clink: High metallic coin cascade
   */
  public playTokenClink(count: number = 3) {
    const ctx = this.getContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    for (let i = 0; i < count; i++) {
      const delay = i * 0.06;
      const freq = 2400 + (i * 220) + (Math.random() * 80);
      this.playMetallicDing(freq, now + delay);
    }
  }

  /**
   * 5. UI Micro Click: Crisp feedback on hover / tap (Bio-Box tap SFX)
   */
  public playUiClick() {
    if (this.isMuted) return;
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (now - this.lastUiClickTime < 35) {
      return; // Deduplicate rapid duplicate clicks (e.g. pointerup + click + inline handler)
    }
    this.lastUiClickTime = now;

    const ctx = this.getContext();
    if (!ctx) return;

    try {
      const audioNow = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(650, audioNow);
      osc.frequency.exponentialRampToValueAtTime(200, audioNow + 0.03);

      gain.gain.setValueAtTime(0.12, audioNow);
      gain.gain.exponentialRampToValueAtTime(0.001, audioNow + 0.03);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(audioNow);
      osc.stop(audioNow + 0.03);
    } catch (e) {
      console.warn('[AudioSynth] playUiClick error:', e);
    }
  }

  // Internal Helper: Play individual tone
  private playTone(
    freq: number, 
    type: OscillatorType, 
    duration: number, 
    volume: number, 
    startTime: number, 
    lowpassCutoff?: number
  ) {
    const ctx = this.getContext();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, startTime);

    gain.gain.setValueAtTime(0.001, startTime);
    gain.gain.linearRampToValueAtTime(volume, startTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

    if (lowpassCutoff) {
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(lowpassCutoff, startTime);
      osc.connect(filter);
      filter.connect(gain);
    } else {
      osc.connect(gain);
    }

    gain.connect(ctx.destination);
    osc.start(startTime);
    osc.stop(startTime + duration);
  }

  // Internal Helper: Metallic coin ding
  private playMetallicDing(freq: number, startTime: number) {
    const ctx = this.getContext();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, startTime);

    gain.gain.setValueAtTime(0.2, startTime);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.12);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(startTime);
    osc.stop(startTime + 0.12);
  }

  // Internal Helper: Celestial sweep
  private playSweep(startFreq: number, endFreq: number, duration: number, startTime: number) {
    const ctx = this.getContext();
    if (!ctx) return;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(startFreq, startTime);
    osc.frequency.exponentialRampToValueAtTime(endFreq, startTime + duration);

    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(startFreq * 2, startTime);
    filter.frequency.exponentialRampToValueAtTime(endFreq * 1.5, startTime + duration);
    filter.Q.setValueAtTime(3, startTime);

    gain.gain.setValueAtTime(0.01, startTime);
    gain.gain.linearRampToValueAtTime(0.12, startTime + duration * 0.5);
    gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

    osc.connect(filter);
    filter.connect(gain);
    gain.connect(ctx.destination);

    osc.start(startTime);
    osc.stop(startTime + duration);
  }
}

export const sfx = new GamificationAudioSynthesizer();
